import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { getQBErrorMessageForToast, removeLoadingKey } from '../helpers/common'
import { toast } from 'react-toastify'
import { enumStatuses } from '../consts/status'
import { getRefreshTokenData } from './quickBooksSlice'

export const PurchaseOrdersLoadingKeys = {
  orders: "ALL_ORDERS",
  update: "UPD_ORDER",
  stats: "ALL_STATS",
  syncPO: "SYNC_PO_ITEM",
  reportByProducts: "GET_PROBLEMATIC_POS_BY_PRODUCTS",
  poNotes: "PO_NOTES",
  addPONote: "ADD_PO_NOTE",
  poAuditLog: "PO_AUDIT_LOG",
  poLedgerDetail: "PO_LEDGER_DETAIL"
}

const initialState = {
    loading: true,
    loadingKeys: [],
    isFirstLoadComplete: false,
    reportDataProducts: [],
    error: null,
    purchaseOrders: [],
    syncingPOs: [],
    syncingQBPOs: [],
    pagination: {
      isTheLastPage: false,
      rangeRows: "",
      totalPages: 0
    },
    stats: {},
    poNotes: [],
    poAuditLog: [],
    poLedgerDetail: null
}

export const getPOLedgerDetailRequest = createAsyncThunk(
  'purchaseOrders/getPOLedgerDetailRequest',
  async ({ poId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/po-ledger/${poId}`);
      return data?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
)

export const upsertPO = createAsyncThunk(
  'purchaseOrders/upsertPO',
  async ({ 
    po, 
    search, 
    statuses, 
    page, 
    itemsPerPage 
  }, { rejectWithValue, dispatch }) => {
    try {
      await axios.post(`/api/v1/purchase-orders`, po);
      dispatch(getAllPO({ search, statuses, page, itemsPerPage }));
      dispatch(getStatsForFinance({}));
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const upsertInvoicesBatch = createAsyncThunk(
  'purchaseOrders/upsertInvoicesBatch',
  async ({
    invoices, // [{ poId, invoice }]
    search,
    statuses,
    page,
    itemsPerPage
  }, { rejectWithValue, dispatch }) => {
    try {
      for (const item of invoices) {
        await axios.post(`/api/v1/purchase-orders`, { id: item.poId, invoice: item.invoice });
      }
      dispatch(getAllPO({ search, statuses, page, itemsPerPage }));
      return { count: invoices.length };
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const syncPOItem = createAsyncThunk(
  'purchaseOrders/syncPOItem',
  async ({ 
    poId, 
    shippingPrice,
    search, 
    statuses, 
    page, 
    itemsPerPage 
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/purchase-order/sync`, { poId, shippingPrice });
      dispatch(getAllPO({ search, statuses, page, itemsPerPage }));
      dispatch(getStatsForFinance({}));
      return data
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const syncPOToQB = createAsyncThunk(
  'purchaseOrders/syncPOToQB',
  async ({ 
    poId, 
    search, 
    statuses,
    page, 
    itemsPerPage
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/quickbooks/upsert-po`, { poId });
      dispatch(getRefreshTokenData({}));
      await dispatch(getAllPO({ search, statuses, page, itemsPerPage }));
      return data
    } catch (error) {
      await dispatch(getRefreshTokenData({}));
      await dispatch(getAllPO({ search, statuses, page, itemsPerPage }));
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const getAllPO = createAsyncThunk(
    'purchaseOrders/getAllPurchases',
    async (params, { rejectWithValue }) => {
      try {
        const { data } = await axios.get(`/api/v1/purchase-orders`, { params: { 
          search: params?.search, 
          statuses: (params?.statuses || []), 
          page: params?.page, 
          itemsPerPage: params?.itemsPerPage 
        } });
        return data;
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.message === "canceled") {
          throw new Error("AbortError");
        }
        if ('string' === typeof error.response.data) {
          return rejectWithValue(error.response.data)
        } else {
          return rejectWithValue(error.response.data.errors || error.response.data.error)
        }
      }
    }
)

export const getProblematicProductsByPos = createAsyncThunk(
  'purchaseOrders/getProblematicProductsByPos',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-report-by-problematic-pos`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const getPONotesRequest = createAsyncThunk(
  'purchaseOrders/getPONotesRequest',
  async ({ poId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/purchase-order-notes', { params: { poId } });
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const addPONoteRequest = createAsyncThunk(
  'purchaseOrders/addPONoteRequest',
  async ({ poId, note }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/purchase-order-notes', { poId, note });
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const deletePONoteRequest = createAsyncThunk(
  'purchaseOrders/deletePONoteRequest',
  async ({ noteId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`/api/v1/purchase-order-notes/${noteId}`);
      return { ...data, noteId };
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const getPOAuditLogRequest = createAsyncThunk(
  'purchaseOrders/getPOAuditLogRequest',
  async ({ poId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/purchase-orders/${encodeURIComponent(poId)}/audit-log`);
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const getStatsForFinance = createAsyncThunk(
  'purchaseOrders/getStatsForFinance',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/finance/stats`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const purchaseOrdersSlice = createSlice({
    name: 'purchaseOrders',
    initialState,
    reducers: {
      updatePOById: (state, action) => {
        const { poId, updatedData } = action.payload;
        const index = state.purchaseOrders.findIndex((po) => po.id === poId);
        if (index !== -1) {
          state.purchaseOrders[index] = {
            ...state.purchaseOrders[index],
            ...updatedData,
          };
        }
      },
      cleanUpPOSidebarData: (state) => {
        state.poNotes = [];
        state.poAuditLog = [];
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(getAllPO.pending, (state) => {
            state.loading = true
            state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.orders]
        })
        .addCase(getAllPO.fulfilled, (state, action) => {
            state.loading = false;
            state.purchaseOrders = action.payload?.items;
            state.pagination.isTheLastPage = action.payload?.isTheLastPage;
            state.pagination.rangeRows = action.payload?.rangeRows;
            state.pagination.totalPages = action.payload?.totalPages;
            state.isFirstLoadComplete = true;
            state.error = null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.orders, state.loadingKeys)
        })
        .addCase(getAllPO.rejected, (state, action) => {
            if (action.error?.message === "AbortError") {
              return;
            }
            state.loading = false;
            state.purchaseOrders = [];
            state.pagination.isTheLastPage = false;
            state.pagination.rangeRows = "";
            state.pagination.totalPages = 0;
            state.isFirstLoadComplete = true;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.orders, state.loadingKeys)
        })
        .addCase(upsertPO.pending, (state) => {
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.update]
        })
        .addCase(upsertPO.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null; 
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.update, state.loadingKeys)
            toast.success(
              `${action.meta?.arg?.po?.poNumber} updated.`, { position: "bottom-right" }
            )
        })
        .addCase(upsertPO.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.update, state.loadingKeys)
            toast.error(
              `PO can't be updated. Error - ${action.error?.message}. Arguments: PO - ${action.meta?.arg?.po?.poNumber}, status: ${action.meta?.arg?.po?.status ? enumStatuses[action.meta?.arg?.po?.status] : ""}`, { position: "bottom-right" }
            )
        })
        .addCase(upsertInvoicesBatch.pending, (state) => {
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.update]
        })
        .addCase(upsertInvoicesBatch.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.update, state.loadingKeys)
            toast.success(
              `${action.payload?.count || 0} invoice number${(action.payload?.count || 0) === 1 ? '' : 's'} updated.`, { position: "bottom-right" }
            )
        })
        .addCase(upsertInvoicesBatch.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.update, state.loadingKeys)
            toast.error(
              `Invoice numbers can't be updated. Error - ${action.error?.message}`, { position: "bottom-right" }
            )
        })
        .addCase(syncPOItem.pending, (state, action) => {
          const poId = action?.meta?.arg?.poId;
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.syncPO]
          state.syncingPOs = [...state.syncingPOs, poId]
        })
        .addCase(syncPOItem.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null; 
            state.syncingPOs = state.syncingPOs.filter((item) => item !== action.meta?.arg?.poId);
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.syncPO, state.loadingKeys)
            toast.success(
              `${action.payload?.po_number} synced.`, { position: "bottom-right" }
            )
        })
        .addCase(syncPOItem.rejected, (state, action) => {
            const poId = action?.meta?.arg?.poId;
            state.loading = false;
            state.error = action.error?.message;
            state.syncingPOs = state.syncingPOs.filter((item) => item !== poId);
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.syncPO, state.loadingKeys)
            toast.error(
              `PO can't be synced. Error - ${action.error?.message}. PO ID - ${action.meta?.arg?.poId}.`, { position: "bottom-right" }
            )
        })
        .addCase(getPONotesRequest.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.poNotes]
        })
        .addCase(getPONotesRequest.fulfilled, (state, action) => {
            state.poNotes = action.payload?.items || [];
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poNotes, state.loadingKeys)
        })
        .addCase(getPONotesRequest.rejected, (state) => {
            state.poNotes = [];
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poNotes, state.loadingKeys)
        })
        .addCase(addPONoteRequest.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.addPONote]
        })
        .addCase(addPONoteRequest.fulfilled, (state, action) => {
            if (action.payload?.item) {
              state.poNotes = [action.payload.item, ...state.poNotes];
            }
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.addPONote, state.loadingKeys)
        })
        .addCase(addPONoteRequest.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.addPONote, state.loadingKeys)
            toast.error(
              `Note can't be added. ${action.payload?.error || ''}`, { position: "bottom-right" }
            )
        })
        .addCase(deletePONoteRequest.fulfilled, (state, action) => {
            state.poNotes = state.poNotes.filter((note) => note.id !== action.payload?.noteId);
        })
        .addCase(deletePONoteRequest.rejected, (state, action) => {
            toast.error(
              `Note can't be deleted. ${action.payload?.error || ''}`, { position: "bottom-right" }
            )
        })
        .addCase(getPOAuditLogRequest.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.poAuditLog]
        })
        .addCase(getPOAuditLogRequest.fulfilled, (state, action) => {
            state.poAuditLog = action.payload?.items || [];
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poAuditLog, state.loadingKeys)
        })
        .addCase(getPOAuditLogRequest.rejected, (state) => {
            state.poAuditLog = [];
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poAuditLog, state.loadingKeys)
        })
        .addCase(getPOLedgerDetailRequest.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.poLedgerDetail]
        })
        .addCase(getPOLedgerDetailRequest.fulfilled, (state, action) => {
            state.poLedgerDetail = action.payload || null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poLedgerDetail, state.loadingKeys)
        })
        .addCase(getPOLedgerDetailRequest.rejected, (state) => {
            state.poLedgerDetail = null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.poLedgerDetail, state.loadingKeys)
        })
        .addCase(getStatsForFinance.pending, (state) => {
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.stats]
        })
        .addCase(getStatsForFinance.fulfilled, (state, action) => {
            state.loading = false;
            state.stats = action.payload;
            state.error = null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.stats, state.loadingKeys)
        })
        .addCase(getStatsForFinance.rejected, (state, action) => {
            state.loading = false;
            state.stats = {};
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.stats, state.loadingKeys)
        })
        .addCase(getProblematicProductsByPos.pending, (state) => {
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.reportByProducts]
        })
        .addCase(getProblematicProductsByPos.fulfilled, (state, action) => {
            state.loading = false;
            state.reportDataProducts = action.payload;
            state.error = null;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.reportByProducts, state.loadingKeys)
        })
        .addCase(getProblematicProductsByPos.rejected, (state, action) => {
            state.loading = false;
            state.stats = {};
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.reportByProducts, state.loadingKeys)
        })
        .addCase(syncPOToQB.pending, (state, action) => {
          const poId = action?.meta?.arg?.poId;
          state.loading = true
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersLoadingKeys.syncPO]
          state.syncingQBPOs = [...state.syncingQBPOs, poId]
        })
        .addCase(syncPOToQB.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null; 
            state.syncingQBPOs = state.syncingQBPOs.filter((item) => item !== action.meta?.arg?.poId);
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.syncPO, state.loadingKeys)
            toast.success(
              `${action.payload?.po_number} synced.`, { position: "bottom-right" }
            )
        })
        .addCase(syncPOToQB.rejected, (state, action) => {
            const poId = action?.meta?.arg?.poId;
            state.loading = false;
            const errorMessage = getQBErrorMessageForToast(action?.payload);
            state.error = errorMessage;
            state.syncingQBPOs = state.syncingQBPOs.filter((item) => item !== poId);
            state.loadingKeys = removeLoadingKey(PurchaseOrdersLoadingKeys.syncPO, state.loadingKeys)
            toast.error(
              `PO can't be synced. Error - ${errorMessage} PO ID - ${action.meta?.arg?.poId}.`, { position: "bottom-right" }
            )
        })
    }
})

export const { updatePOById, cleanUpPOSidebarData } = purchaseOrdersSlice.actions;

export default purchaseOrdersSlice.reducer